import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SuperAdminIcon,
  AccountBalance as AccountantIcon,
  RateReview as ReviewOfficerIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const AdminManagement = () => {
  const { currentUser, currentAdmin, apiCall, token } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: 'admin',
    dateOfExpiry: '',
    department: '',
    position: '',
    bio: ''
  });

  // Fetch admins when pagination params change
  // Placed after fetchAdmins declaration to avoid no-use-before-define

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall(`/admin-management?page=${page + 1}&limit=${rowsPerPage}`);

      if (response.success) {
        setAdmins(response.admins || []);
        setTotalCount(response.pagination?.total || 0);
      } else {
        throw new Error('Failed to fetch admins');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setSnackbar({ open: true, message: 'Failed to fetch admins', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, rowsPerPage]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleCreateAdmin = async () => {
    try {
      const response = await apiCall('/admin-management', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      if (response.success) {
        showSnackbar('Admin created successfully', 'success');
        setOpenDialog(false);
        resetForm();
        fetchAdmins();
      } else {
        throw new Error(response.message || 'Failed to create admin');
      }
    } catch (error) {
      console.error('Error creating admin:', error);
      showSnackbar(error.message || 'Failed to create admin', 'error');
    }
  };

  const handleUpdateAdmin = async () => {
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password; // Don't update password if not provided
      }

      const response = await apiCall(`/admin-management/${selectedAdmin._id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (response.success) {
        showSnackbar('Admin updated successfully', 'success');
        setOpenDialog(false);
        resetForm();
        fetchAdmins();
      } else {
        throw new Error(response.message || 'Failed to update admin');
      }
    } catch (error) {
      console.error('Error updating admin:', error);
      showSnackbar(error.message || 'Failed to update admin', 'error');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) {
      return;
    }

    try {
      const response = await apiCall(`/admin-management/${adminId}`, {
        method: 'DELETE',
      });

      if (response.success) {
        showSnackbar('Admin deleted successfully', 'success');
        fetchAdmins();
      } else {
        throw new Error(response.message || 'Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      showSnackbar(error.message || 'Failed to delete admin', 'error');
    }
  };

  const handleDownloadIdCard = async (adminId) => {
    try {
      const base = (api && api.baseURL) ? api.baseURL : (typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') + '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api'));
      const response = await fetch(`${base}/admin-management/${adminId}/id-card`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `admin-id-card-${adminId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        showSnackbar('ID card downloaded successfully', 'success');
      } else {
        throw new Error('Failed to download ID card');
      }
    } catch (error) {
      console.error('Error downloading ID card:', error);
      showSnackbar('Failed to download ID card', 'error');
    }
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    resetForm();
    setOpenDialog(true);
  };

  const openEditDialog = (admin) => {
    setDialogMode('edit');
    setSelectedAdmin(admin);
    setFormData({
      firstName: admin.firstName || '',
      lastName: admin.lastName || '',
      email: admin.email || '',
      phone: admin.phone || '',
      password: '', // Don't pre-fill password
      role: admin.role || 'admin',
      dateOfExpiry: admin.dateOfExpiry ? admin.dateOfExpiry.split('T')[0] : '',
      department: admin.department || '',
      position: admin.position || '',
      bio: admin.bio || ''
    });
    setOpenDialog(true);
  };

  const openViewDialog = (admin) => {
    setDialogMode('view');
    setSelectedAdmin(admin);
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      role: 'admin',
      dateOfExpiry: '',
      department: '',
      position: '',
      bio: ''
    });
    setSelectedAdmin(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <SuperAdminIcon color="error" />;
      case 'admin':
        return <AdminIcon color="primary" />;
      case 'accountant':
        return <AccountantIcon color="success" />;
      case 'review_officer':
        return <ReviewOfficerIcon color="warning" />;
      default:
        return <AdminIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'admin':
        return 'primary';
      case 'accountant':
        return 'success';
      case 'review_officer':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Super admin has unrestricted access (GodMode)
  const canCreateSuperAdmin = currentAdmin?.role === 'super_admin';
  const canManageAdmins = currentAdmin?.role === 'super_admin' || ['super_admin', 'admin'].includes(currentAdmin?.role);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Admin Management
        </Typography>
        {canManageAdmins && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add New Admin
          </Button>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Avatar</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Employee ID</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No admins found
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => (
                  <TableRow key={admin._id} hover>
                    <TableCell>
                      <Avatar
                        src={admin.avatar}
                        alt={`${admin.firstName} ${admin.lastName}`}
                      >
                        {admin.firstName?.[0]}{admin.lastName?.[0]}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      {admin.firstName} {admin.lastName}
                    </TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(admin.role)}
                        label={admin.role?.replace('_', ' ').toUpperCase()}
                        color={getRoleColor(admin.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{admin.employeeId}</TableCell>
                    <TableCell>{admin.department || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton onClick={() => openViewDialog(admin)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {canManageAdmins && (
                        <Tooltip title="Edit">
                          <IconButton onClick={() => openEditDialog(admin)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Download ID Card">
                        <IconButton onClick={() => handleDownloadIdCard(admin._id)}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      {(canManageAdmins && currentUser?.role === 'super_admin') && (
                        <Tooltip title="Delete">
                          <IconButton 
                            onClick={() => handleDeleteAdmin(admin._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'Create New Admin'}
          {dialogMode === 'edit' && 'Edit Admin'}
          {dialogMode === 'view' && 'Admin Details'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'view' ? (
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Name
                    </Typography>
                    <Typography variant="body1">
                      {selectedAdmin?.firstName} {selectedAdmin?.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{selectedAdmin?.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">{selectedAdmin?.phone || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Role
                    </Typography>
                    <Chip
                      icon={getRoleIcon(selectedAdmin?.role)}
                      label={selectedAdmin?.role?.replace('_', ' ').toUpperCase()}
                      color={getRoleColor(selectedAdmin?.role)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Employee ID
                    </Typography>
                    <Typography variant="body1">{selectedAdmin?.employeeId}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Department
                    </Typography>
                    <Typography variant="body1">{selectedAdmin?.department || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Bio
                    </Typography>
                    <Typography variant="body1">{selectedAdmin?.bio || 'No bio available'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={dialogMode === 'create' ? 'Password' : 'New Password (leave blank to keep current)'}
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  required={dialogMode === 'create'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    label="Role"
                    onChange={(e) => handleFormChange('role', e.target.value)}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="accountant">Accountant</MenuItem>
                    <MenuItem value="review_officer">Review Officer</MenuItem>
                    {canCreateSuperAdmin && (
                      <MenuItem value="super_admin">Super Admin</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Department"
                  value={formData.department}
                  onChange={(e) => handleFormChange('department', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Position"
                  value={formData.position}
                  onChange={(e) => handleFormChange('position', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Expiry"
                  type="date"
                  value={formData.dateOfExpiry}
                  onChange={(e) => handleFormChange('dateOfExpiry', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bio"
                  multiline
                  rows={3}
                  value={formData.bio}
                  onChange={(e) => handleFormChange('bio', e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode !== 'view' && (
            <Button
              onClick={dialogMode === 'create' ? handleCreateAdmin : handleUpdateAdmin}
              variant="contained"
            >
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminManagement;